import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../utils/styledComponent';
import theme from '../../theme';
import yyyymmdd from '../../libs/date_convert';
import useDriverLocationsPoll, {
  DEFAULT_POLL_INTERVAL_MS,
} from '../../hooks/useDriverLocationsPoll';
import HeroSummary from './MonitoringTab/HeroSummary';
import MonitoringFilterBar from './MonitoringTab/MonitoringFilterBar';
import RouteListView from './MonitoringTab/RouteListView';
import DriverDayPane from './MonitoringTab/DriverDayPane';
import GlobalMapPane from './MonitoringTab/GlobalMapPane';
import RouteMapPane from './MonitoringTab/RouteMapPane';
import { scopeDrivers, focusDriver } from './MonitoringTab/monitoringFilters';
import buildDriverOptions from './MonitoringTab/driverOptions';
import buildDriverDay from './MonitoringTab/driverDay';
import { buildPingTrail } from '../../utils/composeDriverRecords';

const StyledDiv = styledComponent('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing.sectionGap,

  '& .mt-error': {
    color: theme.colors.danger,
    fontSize: theme.sizes.bodyFontSize,
    padding: theme.spacing.cardPadding,
  },
  '& .mt-empty-prompt': {
    background: theme.colors.bgCard,
    borderRadius: theme.radii.card,
    border: theme.borders.card,
    boxShadow: theme.shadows.card,
    minHeight: '320px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: '8px',
    padding: theme.spacing.cardPadding,
  },
  '& .mt-empty-title': {
    fontSize: theme.sizes.headingFontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textDark,
  },
  '& .mt-empty-sub': {
    fontSize: theme.sizes.bodyFontSize,
    color: theme.colors.textMuted,
    maxWidth: '420px',
    lineHeight: 1.4,
  },
});

function MonitoringTab({
  region,
  market,
  fcByLocationId,
  stockingDate,
  enabled,
  refetchKey,
  applyKey,
  onLoadingChange,
  onApplyingChange,
}) {
  const stockingDateStr = stockingDate ? yyyymmdd(stockingDate, '-') : null;
  const { data, trailsByDriver, isLoading, error, lastPolledAt } =
    useDriverLocationsPoll({
      stockingDate: stockingDateStr,
      region,
      market,
      fcByLocationId,
      enabled,
      intervalMs: DEFAULT_POLL_INTERVAL_MS,
      refetchKey,
      applyKey,
    });

  // 'list' (default) = full-width route table; 'map' = one driver's day, the
  // Global map under "Show all drivers", or the Single-route map while a route
  // is selected. The map mode is derived
  // from selectedRouteId rather than stored.
  const [viewMode, setViewMode] = useState('list');
  // Which view the supervisor opened the Single-route map from, so its back
  // control returns them there: the list, or the driver's day.
  const [routeOrigin, setRouteOrigin] = useState('list');
  // The driver in focus is an EMAIL — the picker lists people, and one person
  // can appear on several routes. A route selection is a separate focus.
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [selectedDriverEmail, setSelectedDriverEmail] = useState(null);
  const [showAllDrivers, setShowAllDrivers] = useState(false);
  // The stop a click asked the Single-route map to open on, or null. Set only
  // together with a route selection; the pane seeds its own focus from it.
  const [focusedStopId, setFocusedStopId] = useState(null);
  const [locationQuery, setLocationQuery] = useState('');
  const [shiftFilter, setShiftFilter] = useState('all');

  // Shift + location scope the fleet the picker lists; the driver focus
  // narrows it further for the list. The route selection is a separate
  // focus so the picker still offers every scoped driver.
  const scopedDrivers = useMemo(
    () => scopeDrivers(data, { shiftFilter, locationQuery }),
    [data, shiftFilter, locationQuery],
  );
  const filteredDrivers = useMemo(
    () => focusDriver(scopedDrivers, selectedDriverEmail),
    [scopedDrivers, selectedDriverEmail],
  );
  // One picker row per person across the scoped fleet.
  const driverOptions = useMemo(
    () => buildDriverOptions(scopedDrivers),
    [scopedDrivers],
  );
  // The selected driver's whole day, from the UNSCOPED records so narrowing
  // the route filters never hides stops they actually serviced, plus their
  // own readings normalized through the same builder the route trails use.
  const driverDay = useMemo(
    () => buildDriverDay(data, selectedDriverEmail),
    [data, selectedDriverEmail],
  );
  const driverTrail = useMemo(
    () =>
      selectedDriverEmail
        ? buildPingTrail(trailsByDriver[selectedDriverEmail.toLowerCase()])
        : [],
    [trailsByDriver, selectedDriverEmail],
  );
  const selectedRoute = selectedRouteId
    ? filteredDrivers.find((driver) => driver.routeId === selectedRouteId) ||
      null
    : null;

  // A selected route that leaves the filtered set (filter change, route gone
  // from the poll) drops the route focus; the driver focus, if any, stands.
  useEffect(() => {
    if (selectedRouteId && !selectedRoute) {
      setSelectedRouteId(null);
    }
  }, [selectedRouteId, selectedRoute]);

  // Applying filters is a fresh question about a fresh fleet, so the tab
  // answers it where it always starts: the List view, with no route or driver
  // held over from the previous answer. Keyed on applyKey, NOT refetchKey —
  // the header's Refresh bumps refetchKey too, and a refresh must leave the
  // supervisor exactly where they were. The ref skips the mount pass: the
  // first applyKey the tab ever sees is the one it opened with.
  const hasSeenApplyKey = useRef(false);
  useEffect(() => {
    if (!hasSeenApplyKey.current) {
      hasSeenApplyKey.current = true;
      return;
    }
    setViewMode('list');
    setSelectedRouteId(null);
    setSelectedDriverEmail(null);
    setShowAllDrivers(false);
    setFocusedStopId(null);
  }, [applyKey]);

  const rowsForList = selectedRouteId
    ? filteredDrivers.filter((driver) => driver.routeId === selectedRouteId)
    : filteredDrivers;

  // The cleanup resets the parent on unmount so switching tabs mid-poll
  // doesn't strand the parent's loading flag at true (SCT-760).
  useEffect(() => {
    if (onLoadingChange) onLoadingChange(isLoading);
    return () => {
      if (onLoadingChange) onLoadingChange(false);
    };
  }, [isLoading, onLoadingChange]);

  // Which KIND of load is running. The shared Apply button must move for the
  // first load and for filter changes — the 25–30 s ones — and must never
  // move for a background poll tick or a Refresh, which re-fetch the same
  // view quietly. `isLoading` alone can't tell them apart: the hook raises
  // it at the top of every tick. So an Apply (applyKey bumps on Apply alone)
  // or the first enable latches "an apply load is pending", and the latch
  // releases when the load that follows settles.
  const [isApplyLoadPending, setIsApplyLoadPending] = useState(false);
  useEffect(() => {
    setIsApplyLoadPending(enabled);
  }, [enabled, applyKey]);
  useEffect(() => {
    if (!isLoading) {
      setIsApplyLoadPending(false);
    }
  }, [isLoading]);
  const isApplying = isLoading && isApplyLoadPending;

  // Additive to onLoadingChange above: that one feeds the page header's
  // background-refresh cue, this one the shared Apply button. Same unmount
  // guard, for the same reason — a stranded latch locks the button.
  useEffect(() => {
    if (onApplyingChange) onApplyingChange(isApplying);
    return () => {
      if (onApplyingChange) onApplyingChange(false);
    };
  }, [isApplying, onApplyingChange]);

  // Picking a person is route-agnostic: it drops any route focus and shows
  // their whole day on the map. "Show all drivers" (email null) opens the
  // Global map of the whole scoped fleet instead. The route search is hidden
  // on the map, so it's cleared on the way in.
  const handleDriverSelect = (email) => {
    setLocationQuery('');
    setSelectedDriverEmail(email);
    setShowAllDrivers(email === null);
    setSelectedRouteId(null);
    setFocusedStopId(null);
    setViewMode('map');
  };

  // List route link / driver's day → Single-route map, optionally opened
  // straight onto one stop. Remembers the current view so the pane's back
  // control can return to it. A route the focused driver never worked drops
  // the driver focus, so the list behind it shows the route.
  const handleOpenRoute = (routeId, stopId = null) => {
    setRouteOrigin(viewMode);
    setFocusedStopId(stopId);
    setSelectedRouteId(routeId);
    const option = driverOptions.find(
      (driver) => driver.email === selectedDriverEmail,
    );
    if (option && !option.routeIds.includes(routeId)) {
      setSelectedDriverEmail(null);
    }
    setViewMode('map');
  };

  // Back from the Single-route map: drop the route focus and return to the
  // view it was opened from. The DRIVER focus survives — a route opened from
  // someone's day belongs to that day, so back lands on it, not on an empty
  // picker.
  const handleBackFromRoute = () => {
    setSelectedRouteId(null);
    setFocusedStopId(null);
    setViewMode(routeOrigin);
  };

  // The route search is hidden on the map, so a query typed in the list must
  // not keep narrowing the driver picker from somewhere it can't be seen.
  const handleViewModeChange = (mode) => {
    if (mode === 'map') {
      setLocationQuery('');
    }
    setViewMode(mode);
  };

  const handleClearFilters = () => {
    setSelectedDriverEmail(null);
    setShowAllDrivers(false);
    setSelectedRouteId(null);
    setFocusedStopId(null);
    setLocationQuery('');
    setShiftFilter('all');
  };

  // Idle until the supervisor applies filters — avoids loading the whole fleet
  // on mount. The poll hook is disabled (enabled=false) so nothing is fetched.
  if (!enabled) {
    return (
      <StyledDiv>
        <div className="mt-empty-prompt">
          <div className="mt-empty-title">
            Select filters to view live routes
          </div>
          <div className="mt-empty-sub">
            Choose a Region, Market, and Date above, then click{' '}
            <strong>Apply Filters</strong> to load the monitoring view.
          </div>
        </div>
      </StyledDiv>
    );
  }

  let view;
  if (viewMode === 'list') {
    view = (
      <RouteListView
        drivers={rowsForList}
        isLoading={isLoading}
        isApplying={isApplying}
        lastPolledAt={lastPolledAt}
        pollIntervalMs={DEFAULT_POLL_INTERVAL_MS}
        error={error}
        onSelectRoute={handleOpenRoute}
      />
    );
  } else if (selectedRoute) {
    view = (
      // Keyed on the route so a different route mounts a fresh pane: the
      // pane's own view state (sort, focus, hover card) belongs to the route
      // being looked at.
      <RouteMapPane
        key={selectedRouteId}
        driver={selectedRoute}
        lastPolledAt={lastPolledAt}
        isLoading={isLoading}
        pollIntervalMs={DEFAULT_POLL_INTERVAL_MS}
        initialFocusedStopId={focusedStopId}
        onBack={handleBackFromRoute}
      />
    );
  } else if (showAllDrivers) {
    view = (
      <GlobalMapPane
        drivers={filteredDrivers}
        lastPolledAt={lastPolledAt}
        isLoading={isLoading}
        error={error}
        pollIntervalMs={DEFAULT_POLL_INTERVAL_MS}
        fitKey={`global-${applyKey}`}
        onSelectRoute={handleOpenRoute}
        onSelectStop={handleOpenRoute}
      />
    );
  } else {
    view = (
      <DriverDayPane
        day={driverDay}
        trail={driverTrail}
        selectedDriverEmail={selectedDriverEmail}
        lastPolledAt={lastPolledAt}
        isLoading={isLoading}
        pollIntervalMs={DEFAULT_POLL_INTERVAL_MS}
        fitKey={`driver-${selectedDriverEmail || 'none'}-${applyKey}`}
        onOpenRoute={handleOpenRoute}
      />
    );
  }

  return (
    <StyledDiv>
      <HeroSummary drivers={filteredDrivers} />
      <MonitoringFilterBar
        drivers={driverOptions}
        selectedDriverEmail={selectedDriverEmail}
        showAllDrivers={showAllDrivers}
        onSelectDriver={handleDriverSelect}
        selectedRouteId={selectedRouteId}
        locationQuery={locationQuery}
        onLocationQueryChange={setLocationQuery}
        shiftFilter={shiftFilter}
        onShiftChange={setShiftFilter}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        onClear={handleClearFilters}
      />
      {view}
    </StyledDiv>
  );
}

MonitoringTab.propTypes = {
  // Parent passes the raw react-select object ({value, label}) for region.
  region: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  market: PropTypes.arrayOf(PropTypes.object),
  // locationId -> fulfillment center map (OpsLocations-derived); used to
  // resolve each route's FC since LocationRoutes has no usable FC field.
  fcByLocationId: PropTypes.objectOf(PropTypes.string),
  stockingDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
  ]),
  // Gates all data loading: the tab stays idle (prompt) until the parent
  // arms it on Apply Filters.
  enabled: PropTypes.bool,
  refetchKey: PropTypes.number,
  // Bumped by Apply Filters alone (refetchKey also moves on Refresh and
  // Clear All), so only an Apply sends the tab back to its List view.
  applyKey: PropTypes.number,
  onLoadingChange: PropTypes.func,
  // Raised only for the initial load and for Apply — never for a poll tick
  // or a Refresh — so the shared Apply button stays still in the background.
  onApplyingChange: PropTypes.func,
};

MonitoringTab.defaultProps = {
  region: '',
  market: [],
  fcByLocationId: null,
  stockingDate: null,
  enabled: true,
  refetchKey: 0,
  applyKey: 0,
  onLoadingChange: null,
  onApplyingChange: null,
};

export default MonitoringTab;
